import { zodResolver } from "@hookform/resolvers/zod"
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from "@mui/material"
import { CloseOutlined } from "@mui-symbols-material/w300"
import { type ReactElement, useCallback, useEffect, useMemo } from "react"
import { Controller, FormProvider, useForm } from "react-hook-form"

import { Button } from "@/components/common/Button"
import { DEFAULT_AUTOCOMPLETE_VALUE, FIELD_LABEL_CLASSES, SELECT_STYLING_PROPS } from "@/constants/forms"
import {
  type CompanyLocationAssociation,
  type Location,
  type ProductOrigin as ProductOriginOriginal,
  type Region,
} from "@/graphql/codegen/graphql"
import type { ProductOrigin } from "@/screens/Products/components/Product/Information/hooks/useProductOrigin"
import { ProductOriginForm } from "@/screens/Products/components/Product/Information/Origin/ProductOriginForm"
import type { ProductOriginFormSchema } from "@/screens/Products/components/Product/Information/schemas/ProductOriginSchema"
import { ProductOriginSchema } from "@/screens/Products/components/Product/Information/schemas/ProductOriginSchema"
import type { ProductOriginSubmissionType } from "@/screens/Products/components/Product/Information/types/productOrigin"
import {
  getOriginTypeValue,
  transformProductOriginSubmitData,
} from "@/screens/Products/components/Product/Information/utils/productOrigin"
import type { AutocompleteOption } from "@/types/form-fields"
import { formatLocationAddress } from "@/utils/utils"

interface ProductOriginDialogProps {
  shippingOriginLocations: CompanyLocationAssociation[]
  companyManufacturingOriginLocations: Location[]
  onClose: () => void
  onSubmit: (data: {
    manufacturingOrigin: ProductOriginSubmissionType
    countryOrigin: ProductOriginSubmissionType
    shippingOrigin: string
  }) => void
  productOrigin: ProductOrigin | null
}

export const ProductOriginDialog: React.FC<ProductOriginDialogProps> = ({
  shippingOriginLocations,
  companyManufacturingOriginLocations,
  onClose,
  onSubmit,
  productOrigin,
}): ReactElement => {
  const hasOrigin = useMemo(
    () => (productOrigin?.locationsAssociations?.length ?? 0) > 0,
    [productOrigin?.locationsAssociations]
  )

  const getCurrentRegionOption = useCallback(
    (region: Region): typeof DEFAULT_AUTOCOMPLETE_VALUE | AutocompleteOption => {
      return {
        label: region?.name || "",
        id: region?.isoCode || "",
      }
    },
    []
  )

  const getOriginRegion = useCallback(
    (origin: ProductOriginOriginal | null) => {
      const originRegion = (origin?.region ?? origin?.exactLocation?.region) as Region
      return getCurrentRegionOption(originRegion)
    },
    [getCurrentRegionOption]
  )

  const initialData = useMemo(() => {
    if (!hasOrigin) return null
    const { manufacturingOrigin, countryOrigin } = productOrigin || {}
    return {
      shippingOrigin: productOrigin?.locationsAssociations[0]?.locationAssociationId || "",
      manufacturingOrigin: {
        exactLocation: manufacturingOrigin?.exactLocationId || null,
        country: manufacturingOrigin?.country?.isoCode || null,
        region: getOriginRegion(manufacturingOrigin as ProductOriginOriginal) || DEFAULT_AUTOCOMPLETE_VALUE,
        city: manufacturingOrigin?.city || null,
      },
      countryOrigin: {
        exactLocation: countryOrigin?.exactLocationId || null,
        country: countryOrigin?.country?.isoCode || null,
        region: getOriginRegion(countryOrigin as ProductOriginOriginal) || DEFAULT_AUTOCOMPLETE_VALUE,
        city: countryOrigin?.city || null,
      },
    }
  }, [productOrigin, hasOrigin, getOriginRegion])

  const methods = useForm<ProductOriginFormSchema>({
    resolver: zodResolver(ProductOriginSchema),
    defaultValues: {
      shippingOrigin: "",
      manufacturingOrigin: {
        exactLocation: null,
        originType: undefined,
        region: DEFAULT_AUTOCOMPLETE_VALUE,
        city: "",
        country: "",
      },
      countryOrigin: {
        exactLocation: null,
        originType: undefined,
        region: DEFAULT_AUTOCOMPLETE_VALUE,
        city: "",
        country: "",
      },
    },
  })
  const {
    control,
    handleSubmit,
    formState: { isValid },
    reset,
  } = methods

  const genericOptionList = shippingOriginLocations.map(({ companyLocationAssociationId, shipping }) => ({
    label: formatLocationAddress(shipping),
    companyLocationAssociationId,
    companyId: shipping?.companyId,
  }))

  const handleClose = useCallback(() => {
    reset()
    onClose()
  }, [onClose, reset])

  const onFormSubmit = useCallback(
    (data: ProductOriginFormSchema) => {
      const { manufacturingOrigin, countryOrigin, ...submitData } = data

      const finalizedData = {
        ...submitData,
        manufacturingOrigin: transformProductOriginSubmitData(manufacturingOrigin),
        countryOrigin: transformProductOriginSubmitData(countryOrigin),
      }

      onSubmit(finalizedData)
      handleClose()
    },
    [onSubmit, handleClose]
  )

  useEffect(() => {
    reset({
      shippingOrigin: initialData?.shippingOrigin || "",
      manufacturingOrigin: {
        country: initialData?.manufacturingOrigin?.country || "",
        region:
          getOriginRegion(productOrigin?.manufacturingOrigin as ProductOriginOriginal) || DEFAULT_AUTOCOMPLETE_VALUE,
        city: initialData?.manufacturingOrigin?.city || "",
        originType:
          getOriginTypeValue(
            initialData?.manufacturingOrigin as ProductOriginFormSchema["manufacturingOrigin"],
            companyManufacturingOriginLocations
          ) ?? undefined,
        exactLocation: initialData?.manufacturingOrigin?.exactLocation || null,
      },
      countryOrigin: {
        country: initialData?.countryOrigin?.country || "",
        region: getOriginRegion(productOrigin?.countryOrigin as ProductOriginOriginal) || DEFAULT_AUTOCOMPLETE_VALUE,
        city: initialData?.countryOrigin?.city || "",
        originType:
          getOriginTypeValue(
            initialData?.countryOrigin as ProductOriginFormSchema["manufacturingOrigin"],
            companyManufacturingOriginLocations
          ) ?? undefined,
        exactLocation: initialData?.countryOrigin?.exactLocation || null,
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Dialog open={true} onClose={handleClose} maxWidth='sm' fullWidth data-testid='product-origin-dialog'>
      <DialogTitle className='pb-3 font-normal text-primary' data-testid='dialog-title'>
        Product Origin
      </DialogTitle>
      <IconButton
        aria-label='close'
        onClick={handleClose}
        className='absolute right-4 top-4 text-primary'
        data-testid='close-button'
      >
        <CloseOutlined />
      </IconButton>
      <Divider />
      <DialogContent classes={{ root: "p-10" }}>
        <form onSubmit={handleSubmit(onFormSubmit)} className='flex flex-col gap-10' data-testid='product-origin-form'>
          <FormProvider {...methods}>
            <Box>
              <Typography variant='body1' fontWeight={500} className='leading-6 tracking-[0.15px] text-gray-700'>
                1. Shipping Origin
              </Typography>
              <Controller
                name='shippingOrigin'
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <label className={FIELD_LABEL_CLASSES}>
                      Ship From <span className='text-red-600'>*</span>
                    </label>
                    <Select
                      {...field}
                      {...SELECT_STYLING_PROPS}
                      renderValue={(selected) => {
                        if (!selected) {
                          return <Typography color='textDisabled'>Select One</Typography>
                        }
                        const location = shippingOriginLocations.find(
                          ({ companyLocationAssociationId }) => companyLocationAssociationId === selected
                        )?.shipping
                        return formatLocationAddress(location)
                      }}
                      data-testid='shipping-origin-select'
                    >
                      {genericOptionList.map(({ label, companyLocationAssociationId }, i) => (
                        <MenuItem key={label + i} value={companyLocationAssociationId}>
                          <Box className='overflow-auto text-wrap'>{label}</Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Box>
            <Box className='flex flex-col gap-3'>
              <ProductOriginForm
                title='2. Country of Origin'
                selectTypeLabel='Country of origin location'
                selectLocationLabel='Country of origin location'
                parentFieldName='countryOrigin'
                testIdPrefix='country-origin'
              />
            </Box>
            <Box className='flex flex-col gap-3'>
              <ProductOriginForm
                title='3. Manufacturing Origin'
                selectTypeLabel='Manufacturing origin location'
                selectLocationLabel='Supplier’s Location'
                parentFieldName='manufacturingOrigin'
                testIdPrefix='manufacturing-origin'
              />
            </Box>
          </FormProvider>
        </form>
      </DialogContent>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button appearance='outlined' onClick={handleClose}>
          Cancel
        </Button>
        <Button type='submit' disabled={!isValid} onClick={handleSubmit(onFormSubmit)}>
          {initialData ? "Update" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
